import { apiClient, getErrorMessage } from './apiClient'
import * as indexedDB from './indexedDBService'
import { addToSyncQueue } from './syncService'
import type { Pedido, PedidoGrupoIndustria, TipoFrete } from '../types'
import type { PaginationResponse } from './clienteService'

export interface PedidoFilters {
  page?: number
  limit?: number
  search?: string
  status?: 'all' | 'pendente' | 'processando' | 'concluido' | 'cancelado'
  clienteId?: string
  industriaId?: string // Filtrar pedidos que contenham uma indústria
  dataInicio?: string
  dataFim?: string
  sort?: string
  groupBy?: 'industria' // Agrupar resultados por indústria (modo legado)
  subpedidos?: boolean // Retornar subpedidos por indústria
}

export interface PedidoItemRequest {
  produtoId: string
  quantidade: number
  preco?: number
}

export interface PedidoIndustriaRequest {
  industriaId: string
  observacao?: string // Observação específica para esta indústria
  observacaoIndustria?: string
  observacao_industria?: string
  ordemCompra?: string // Ordem de compra específica da indústria
  frete?: TipoFrete // Tipo de frete específico da indústria
  itens: PedidoItemRequest[]
}

export interface PedidoCreateRequest {
  clienteId: string
  observacao?: string // Observação geral do pedido
  ordemCompra?: string // Número da ordem de compra (legado / padrão)
  frete?: TipoFrete // Tipo de frete (legado / padrão)
  industrias: PedidoIndustriaRequest[]
}

export interface PedidoUpdateStatusRequest {
  status: 'pendente' | 'processando' | 'concluido' | 'cancelado'
}

export const pedidoService = {
  async getAll(filters?: PedidoFilters): Promise<PaginationResponse<Pedido> | PaginationResponse<PedidoGrupoIndustria>> {
    try {
      if (navigator.onLine) {
        const params = new URLSearchParams()
        if (filters?.page) params.append('page', filters.page.toString())
        if (filters?.limit) params.append('limit', filters.limit.toString())
        if (filters?.search) params.append('search', filters.search)
        if (filters?.status && filters.status !== 'all') params.append('status', filters.status)
        if (filters?.clienteId) params.append('clienteId', filters.clienteId)
        if (filters?.industriaId) params.append('industriaId', filters.industriaId)
        if (filters?.dataInicio) params.append('dataInicio', filters.dataInicio)
        if (filters?.dataFim) params.append('dataFim', filters.dataFim)
        if (filters?.sort) params.append('sort', filters.sort)
        if (filters?.groupBy) params.append('groupBy', filters.groupBy)
        if (filters?.subpedidos) params.append('subpedidos', 'true')

        const response = await apiClient.get<PaginationResponse<Pedido> | PaginationResponse<PedidoGrupoIndustria>>(`/pedidos?${params}`)
        return response.data
      }

      // Offline: retorna pedidos pendentes do IndexedDB
      const pedidosPendentes = await indexedDB.getAll<Pedido>(indexedDB.STORES.PEDIDOS_PENDENTES)

      return {
        data: pedidosPendentes,
        pagination: {
          page: 1,
          limit: pedidosPendentes.length,
          total: pedidosPendentes.length,
          totalPages: 1,
        }
      }
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async getById(id: string): Promise<Pedido> {
    try {
      if (navigator.onLine) {
        const response = await apiClient.get<Pedido>(`/pedidos/${id}`)
        return response.data
      }

      // Tenta buscar dos pedidos pendentes
      const pedido = await indexedDB.get<Pedido>(indexedDB.STORES.PEDIDOS_PENDENTES, id)
      if (!pedido) throw new Error('Pedido não encontrado')
      return pedido
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async getSubpedidosPorIndustria(id: string): Promise<Pedido[]> {
    try {
      if (navigator.onLine) {
        const response = await apiClient.get<Pedido[]>(`/pedidos/${id}/por-industria`)
        return response.data
      }

      // Offline: retorna o pedido único como fallback
      const pedido = await indexedDB.get<Pedido>(indexedDB.STORES.PEDIDOS_PENDENTES, id)
      if (!pedido) throw new Error('Pedido não encontrado')
      return [pedido]
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async create(data: PedidoCreateRequest): Promise<Pedido[]> {
    try {
      if (navigator.onLine) {
        const response = await apiClient.post<{ pedidos: Pedido[] }>('/pedidos', data)
        return response.data.pedidos || []
      }

      // Offline: salva pedido localmente com status pendente
      const tempId = `offline_${Date.now()}`
      const pedido: Pedido = {
        id: tempId,
        ...data,
        status: 'pendente',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        _offline: true, // Marca como criado offline
      } as any

      await indexedDB.put(indexedDB.STORES.PEDIDOS_PENDENTES, pedido)
      await addToSyncQueue('CREATE', 'pedido', data)
      
      console.log('📦 Pedido salvo offline:', pedido)
      return [pedido]
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async update(id: string, data: PedidoCreateRequest): Promise<Pedido> {
    try {
      if (navigator.onLine) {
        const response = await apiClient.put<Pedido | { pedido: Pedido }>(`/pedidos/${id}`, data)
        const pedido = 'pedido' in response.data ? response.data.pedido : response.data
        return pedido
      }

      // Offline: atualiza pedido localmente
      const pedido = await indexedDB.get<Pedido>(indexedDB.STORES.PEDIDOS_PENDENTES, id)
      if (!pedido) throw new Error('Pedido não encontrado')

      const updated = {
        ...pedido,
        ...data,
        updatedAt: new Date().toISOString(),
      }

      await indexedDB.put(indexedDB.STORES.PEDIDOS_PENDENTES, updated)
      await addToSyncQueue('UPDATE', 'pedido', { id, ...data })
      
      return updated
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async updateStatus(id: string, status: PedidoUpdateStatusRequest['status']): Promise<Pedido> {
    try {
      if (navigator.onLine) {
        const response = await apiClient.patch<Pedido | { pedido: Pedido }>(`/pedidos/${id}/status`, { status })
        const pedido = 'pedido' in response.data ? response.data.pedido : response.data
        return pedido
      }

      // Offline: atualiza status localmente
      const pedido = await indexedDB.get<Pedido>(indexedDB.STORES.PEDIDOS_PENDENTES, id)
      if (!pedido) throw new Error('Pedido não encontrado')

      const updated = {
        ...pedido,
        status,
        updatedAt: new Date().toISOString(),
      }

      await indexedDB.put(indexedDB.STORES.PEDIDOS_PENDENTES, updated)
      await addToSyncQueue('UPDATE', 'pedido', { id, status })
      
      return updated
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async delete(id: string, excluirGrupo: boolean = false): Promise<void> {
    try {
      if (navigator.onLine) {
        const params = excluirGrupo ? '?excluirGrupo=true' : ''
        await apiClient.delete(`/pedidos/${id}${params}`)
        return
      }

      // Offline: remove localmente
      await indexedDB.remove(indexedDB.STORES.PEDIDOS_PENDENTES, id)
      await addToSyncQueue('DELETE', 'pedido', { id, excluirGrupo })
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },

  async exportarRelatorio(filters?: PedidoFilters): Promise<Blob> {
    try {
      const params = new URLSearchParams()
      params.append('formato', 'xlsx')
      if (filters?.clienteId) params.append('clienteId', filters.clienteId)
      if (filters?.industriaId) params.append('industriaId', filters.industriaId)
      if (filters?.dataInicio) params.append('dataInicio', filters.dataInicio)
      if (filters?.dataFim) params.append('dataFim', filters.dataFim)
      if (filters?.status && filters.status !== 'all') params.append('status', filters.status)
      if (filters?.search) params.append('search', filters.search)

      const response = await apiClient.get<Blob>(`/pedidos/relatorio/exportar?${params}`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  },
}
